import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import type { Organization, Project, ProjectScope } from '@/lib/schemas';
import type { InvoiceChangeIntent } from '@/lib/invoiceSelectiveHistoryContracts';

type SelectedProject = {
  project: Project;
  scopes: ProjectScope[];
  scopeIds: string[];
};

/** Values typed on the client page before she chose "A project is moving to a different client" (R2 AC4). */
export interface CarriedClientDetails {
  displayName?: string;
  legalName?: string;
  billingAddress?: string;
}

export interface ProjectPayerChoiceProps {
  /** The project she is on (Bill To entry). It is chosen when the flow opens. Absent from the client page. */
  projectId?: string;
  /** The source client: the one whose project is moving. */
  organizationId: string;
  onIntent: (intent: InvoiceChangeIntent, focusedProjectIds: string[]) => void;
  onCancel: () => void;
  /** Client page entry: goes back to the "What changed?" question. */
  onBack?: () => void;
  /** Client page entry: what she typed there, offered again when she creates the new client. */
  carried?: CarriedClientDetails;
}

const PAGE = 30;
const inputClass = 'block w-full rounded border border-slate-300 p-2 text-sm';

/** Selects named source projects and the new client before invoice review starts. */
export function ProjectPayerChoice({ projectId, organizationId, onIntent, onCancel, onBack, carried }: ProjectPayerChoiceProps) {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [projects, setProjects] = useState<Project[]>([]);
  const [otherMatches, setOtherMatches] = useState<Project[]>([]);
  const [projectTotal, setProjectTotal] = useState(0);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [selected, setSelected] = useState<Record<string, SelectedProject>>({});
  const [loadingProjectId, setLoadingProjectId] = useState<string | null>(null);
  const [projectLoading, setProjectLoading] = useState(true);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [currentProjectError, setCurrentProjectError] = useState<string | null>(null);
  const [targetSearch, setTargetSearch] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [sourceOrganization, setSourceOrganization] = useState<Organization | null>(null);
  const [target, setTarget] = useState<Organization | null>(null);
  const [organizationLoading, setOrganizationLoading] = useState(true);
  const [organizationError, setOrganizationError] = useState<string | null>(null);
  const [choiceError, setChoiceError] = useState<string | null>(null);

  // ---- Create a new client (R2 AC6) ----
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState(carried?.legalName ?? '');
  const [createLegal, setCreateLegal] = useState(carried?.legalName ?? '');
  const [createAddress, setCreateAddress] = useState(carried?.billingAddress ?? '');
  const [createMatches, setCreateMatches] = useState<Organization[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const creatingRef = useRef(false);
  const preselectedRef = useRef(false);

  const sourceName = sourceOrganization?.name || 'This client';

  // The current project (Bill To entry) is chosen as soon as it is read.
  useEffect(() => {
    if (!projectId) return;
    let active = true;
    api.projects.get(projectId).then((result) => {
      if (!active) return;
      setCurrentProject(result.project);
      if (!preselectedRef.current) {
        preselectedRef.current = true;
        void addProject(result.project);
      }
    }).catch(() => {
      if (active) setCurrentProjectError('Could not load the current project. Search for it by name.');
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Only projects that have a client billing track of the source client can move from here (finding r).
  useEffect(() => {
    let active = true;
    setProjectLoading(true);
    setProjectError(null);
    const term = search.trim();
    api.projects.list({ search: term || undefined, limit: PAGE, offset, is_template: false, include_archived: true,
      client_organization_id: organizationId }).then(async (result) => {
      if (!active) return;
      setProjects(result.projects);
      setProjectTotal(result.total);
      setProjectLoading(false);
      // A search started from the wrong client is never a silent miss: projects of other clients that match are
      // listed below, not selectable, with where to go.
      if (term) {
        try {
          const wider = await api.projects.list({ search: term, limit: 8, is_template: false, include_archived: true });
          if (!active) return;
          const mine = new Set(result.projects.map((project) => project.id));
          setOtherMatches(wider.projects.filter((project) => !mine.has(project.id)
            && project.organization_id !== organizationId));
        } catch {
          if (active) setOtherMatches([]);
        }
      } else {
        setOtherMatches([]);
      }
    }).catch(() => {
      if (!active) return;
      setProjectError('Could not search projects. Retry the search before continuing.');
      setProjects([]);
      setOtherMatches([]);
      setProjectLoading(false);
    });
    return () => { active = false; };
  }, [search, offset, organizationId]);

  useEffect(() => {
    let active = true;
    api.organizations.get(organizationId).then((result) => {
      if (active) setSourceOrganization(result.organization);
    }).catch(() => {
      if (active) setSourceOrganization(null);
    });
    return () => { active = false; };
  }, [organizationId]);

  useEffect(() => {
    let active = true;
    setOrganizationLoading(true);
    setOrganizationError(null);
    api.organizations.list({ search: targetSearch, limit: 30 }).then((result) => {
      if (!active) return;
      setOrganizations(result.organizations.filter((org) => org.id !== organizationId));
      setOrganizationLoading(false);
    }).catch(() => {
      if (!active) return;
      setOrganizationError('Could not search target clients. Retry before continuing.');
      setOrganizations([]);
      setOrganizationLoading(false);
    });
    return () => { active = false; };
  }, [targetSearch, organizationId]);

  // Likely matches for the name she is about to create, so she does not make a duplicate.
  useEffect(() => {
    if (!createOpen) return;
    const term = createName.trim();
    if (term.length < 3) { setCreateMatches([]); return; }
    let active = true;
    api.organizations.list({ search: term, limit: 5 }).then((result) => {
      if (active) setCreateMatches(result.organizations.filter((org) => org.id !== organizationId));
    }).catch(() => { if (active) setCreateMatches([]); });
    return () => { active = false; };
  }, [createOpen, createName, organizationId]);

  async function addProject(project: Project) {
    if (selected[project.id] || loadingProjectId) return;
    setChoiceError(null);
    setLoadingProjectId(project.id);
    try {
      const response = await api.projectScopes.list(project.id);
      const scopes = response.scopes.filter((scope) => scope.is_client_type
        && scope.organization_id === organizationId);
      if (scopes.length === 0) {
        setChoiceError(`${project.name} is not billed to ${sourceName}, so it cannot be moved from here.`);
        return;
      }
      setSelected((prior) => ({ ...prior, [project.id]: {
        project, scopes, scopeIds: scopes.length === 1 ? [scopes[0].id] : [],
      } }));
    } catch {
      setChoiceError(`Could not load who ${project.name} is billed to. Try again.`);
    } finally {
      setLoadingProjectId(null);
    }
  }

  function toggleScope(projectIdToChange: string, scopeId: string) {
    setSelected((prior) => {
      const row = prior[projectIdToChange];
      if (!row) return prior;
      return { ...prior, [projectIdToChange]: { ...row,
        scopeIds: row.scopeIds.includes(scopeId)
          ? row.scopeIds.filter((id) => id !== scopeId) : [...row.scopeIds, scopeId],
      } };
    });
  }

  async function createClient() {
    // Synchronous guard: a double click must create exactly one client.
    if (creatingRef.current) return;
    if (!createName.trim()) return;
    creatingRef.current = true;
    setCreating(true);
    setCreateError(null);
    try {
      const result = await api.organizations.create({
        name: createName.trim(),
        legal_name: createLegal.trim() || undefined,
        billing_address: createAddress.trim() || undefined,
        is_client: true, is_self: false, is_consultant: false, is_government: false,
      });
      setTarget(result.organization);
      setOrganizations((prior) => [result.organization, ...prior.filter((org) => org.id !== result.organization.id)]);
      setCreateOpen(false);
    } catch (cause) {
      setCreateError(cause instanceof Error && cause.message ? cause.message : 'Could not create the client. Try again.');
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  }

  const selectedRows = useMemo(() => Object.values(selected)
    .sort((a, b) => a.project.name.localeCompare(b.project.name) || a.project.id.localeCompare(b.project.id)), [selected]);
  const valid = selectedRows.length > 0 && selectedRows.every((row) => row.scopeIds.length > 0)
    && !!target && !projectError && !organizationError && !loadingProjectId;

  // R2 AC8/AC11: a disabled "Review selected projects" says why, next to the button.
  const payerReason = (() => {
    if (loadingProjectId) return null;
    if (selectedRows.length === 0) return 'Choose a project to move.';
    if (!selectedRows.every((row) => row.scopeIds.length > 0)) return 'Tick which billing to move for each project.';
    if (!target) return 'Choose the new client.';
    if (projectError || organizationError) return 'Fix the problem shown above first.';
    return null;
  })();

  function continueToReview() {
    if (!valid) return;
    const projectIds = selectedRows.map((row) => row.project.id);
    const scopeIds = selectedRows.flatMap((row) => row.scopeIds).sort();
    onIntent({ intent: 'project_assignment', organization_id: organizationId,
      target_organization_id: target!.id, project_ids: projectIds,
      client_scope_ids: scopeIds, future_defaults: true }, projectIds);
  }

  const availableProjects = currentProject && !search.trim() && !projects.some((project) => project.id === currentProject.id)
    ? [currentProject, ...projects] : projects;
  const typedDisplayName = carried?.displayName?.trim();
  const showDisplayNameHint = !!typedDisplayName && typedDisplayName !== (carried?.legalName ?? '').trim();

  return <section className="space-y-4" aria-label="Project to move">
    <h2 className="font-semibold">Project to move</h2>
    <p>{`Moving a project from ${sourceName}. Choose the project, then the new client.`}</p>
    <p data-testid="project-payer-existing-invoices-note">You can also choose which existing invoices change.</p>
    <label className="block">Find a project
      <input aria-label="Find a project by number, name or address" value={search} onChange={(event) => {
        setSearch(event.target.value); setOffset(0);
      }} placeholder="Number, name or address, for example A-197" className={inputClass} />
    </label>
    {projectLoading && <p role="status">Loading projects…</p>}
    {projectError && <p role="alert">{projectError}</p>}
    {currentProjectError && <p role="alert">{currentProjectError}</p>}
    {!projectLoading && !projectError && availableProjects.length === 0 && <div className="space-y-2" data-testid="no-movable-projects">
      <p>{search.trim()
        ? `None of the projects of ${sourceName} match "${search.trim()}".`
        : `${sourceName} has no projects that can move to another client.`}</p>
      {onBack && <Button type="button" variant="outline" onClick={onBack}>Back to "What changed?"</Button>}
    </div>}
    {!projectError && <div className="max-h-48 space-y-1 overflow-auto">
      {availableProjects.map((project) => <Button type="button" variant="outline" key={project.id}
        onClick={() => void addProject(project)} disabled={!!selected[project.id] || !!loadingProjectId}
        className="h-auto w-full justify-start whitespace-normal py-2 text-left">
        {project.name} {project.project_number && `(${project.project_number})`}
        {project.client_name && ` · billed to ${project.client_name}`}
        {project.workflow_stage === 'complete' && ' · Complete'}
        {project.is_archived && ' · Archived'}
        {selected[project.id] && ' · selected'}
      </Button>)}
    </div>}
    {otherMatches.length > 0 && <ul className="space-y-1 text-sm text-slate-700">
      {otherMatches.map((project) => <li key={project.id} data-testid={`other-client-match-${project.id}`}>
        {`${projectLabel(project)} is billed to ${project.client_name || 'another client'}. Open ${project.client_name || 'that client'} to move it.`}
      </li>)}
    </ul>}
    {projectTotal > PAGE && <div className="flex gap-2">
      <Button type="button" variant="outline" onClick={() => setOffset(Math.max(0, offset - PAGE))} disabled={offset === 0}>Previous projects</Button>
      <Button type="button" variant="outline" onClick={() => setOffset(offset + PAGE)} disabled={offset + PAGE >= projectTotal}>Next projects</Button>
    </div>}
    {loadingProjectId && <p role="status">Loading this project…</p>}
    {choiceError && <p role="alert">{choiceError}</p>}
    <div role="region" aria-label="Chosen projects" className="space-y-2" data-testid="chosen-projects">
      <h3 className="font-medium">Chosen projects</h3>
      {selectedRows.length === 0 && <p>No projects selected.</p>}
      {selectedRows.map(({ project, scopes, scopeIds }) => <div key={project.id} className="rounded border p-2">
        <div className="flex items-center justify-between gap-2"><strong>{project.name} ({project.project_number || 'no project number'}){project.is_archived && ' · Archived'}</strong>
          <Button type="button" variant="outline" size="sm" aria-label={`Remove ${project.name}`} onClick={() => setSelected((prior) => {
            const next = { ...prior }; delete next[project.id]; return next;
          })}>Remove</Button></div>
        {scopes.length > 1 && <p>This project is billed to this client more than once. Tick the billing to move:</p>}
        {scopes.map((scope, index) => <label key={scope.id} className="block">
          <input type="checkbox" checked={scopeIds.includes(scope.id)}
            onChange={() => toggleScope(project.id, scope.id)} />
          {' '}Billed to {scope.organization_name || sourceOrganization?.name || sourceName} on {projectLabel(project)}
          {scopes.length > 1 && ` · ${scope.scope_type_name || scope.scope_type_slug || 'Client'}${scope.establishment_name ? ` · ${scope.establishment_name}` : ''} (${index + 1} of ${scopes.length})`}
        </label>)}
      </div>)}
    </div>
    <h3 className="font-medium">New client</h3>
    {target && <p data-testid="new-client-chosen">Moving to {target.name}.</p>}
    <label className="block">Find target client
      <input aria-label="Find target client" value={targetSearch} onChange={(event) => setTargetSearch(event.target.value)}
        className={inputClass} />
    </label>
    {organizationLoading && <p role="status">Loading clients…</p>}
    {organizationError && <p role="alert">{organizationError}</p>}
    {!organizationLoading && !organizationError && organizations.length === 0 && <p>No clients found.</p>}
    {!organizationError && <div className="max-h-40 space-y-1 overflow-auto">
      {organizations.map((org) => <Button type="button" variant="outline" key={org.id}
        onClick={() => setTarget(org)} aria-pressed={target?.id === org.id}
        className="h-auto w-full justify-start whitespace-normal py-2 text-left">{org.name}{target?.id === org.id && ' · selected'}</Button>)}
    </div>}
    {!createOpen && <Button type="button" variant="outline" onClick={() => setCreateOpen(true)}>Create a new client</Button>}
    {createOpen && <div role="group" aria-label="Create a new client" className="space-y-3 rounded border p-3">
      <h3 className="font-medium">Create a new client</h3>
      {showDisplayNameHint && <p className="text-sm" data-testid="typed-display-name-hint">
        {`You typed ${typedDisplayName}. If this is a person, add them as a recipient after the move.`}
      </p>}
      <label className="block text-sm">Client name
        <input aria-label="Client name" value={createName} onChange={(event) => setCreateName(event.target.value)}
          className={inputClass} />
      </label>
      <p className="text-sm text-slate-600">Use the business name, not a person. Add people as contacts after.</p>
      <label className="block text-sm">Legal name (optional)
        <input aria-label="Legal name" value={createLegal} onChange={(event) => setCreateLegal(event.target.value)}
          className={inputClass} />
      </label>
      <label className="block text-sm">Billing address (optional)
        <input aria-label="Billing address" value={createAddress} onChange={(event) => setCreateAddress(event.target.value)}
          className={inputClass} />
      </label>
      {createMatches.length > 0 && <div className="space-y-1" data-testid="create-matches">
        <p className="text-sm font-medium">These clients already exist. Use one instead of making a copy.</p>
        {createMatches.map((match) => <div key={match.id} data-testid={`create-match-${match.id}`}
          className="flex items-center justify-between gap-2 text-sm">
          <span>{match.name}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => { setTarget(match); setCreateOpen(false); }}>Use this client</Button>
        </div>)}
      </div>}
      {createError && <p role="alert" className="text-sm text-red-700">{createError}</p>}
      {!createName.trim() && <p className="text-sm text-slate-700" data-testid="create-reason">Type the client name first.</p>}
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); setCreateError(null); }}>Cancel new client</Button>
        <Button type="button" onClick={() => void createClient()} disabled={!createName.trim() || creating}>Create client</Button>
      </div>
    </div>}
    {/* Kept in view at the bottom of the dialog, so Cancel and Review selected projects never scroll out of sight. */}
    <div className="sticky bottom-0 -mx-6 -mb-6 flex flex-col items-end gap-2 border-t bg-white px-6 py-3">
      {payerReason && <p className="text-right text-sm text-slate-700" role="status" data-testid="payer-reason">{payerReason}</p>}
      <div className="flex gap-2">
        {onBack && <Button type="button" variant="outline" onClick={onBack}>Back</Button>}
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="button" onClick={continueToReview} disabled={!valid}>Review selected projects</Button>
      </div>
    </div>
  </section>;
}

function projectLabel(project: Project) {
  return [project.project_number, project.name].filter(Boolean).join(' ');
}
